/**
 * mortgageSmartFinder.js
 * Pure rules engine for the Mortgage Smart Wizard.
 * Input: wizard answers + rate candidates + bank metadata
 * Output: recommended bank, alternatives, reasons[], warnings[], eligibility
 *
 * No React dependencies — fully testable.
 */

import { calculateMortgage } from '../components/Mortgages/mortgageCalcEngine';

/**
 * @typedef {Object} WizardAnswers
 * @property {'buy'|'refi'} flow
 * @property {string} province - 2-letter code ('QC', 'ON', etc.)
 * @property {number} [price] - Property price (buy flow)
 * @property {number} [downPayment] - Down payment (buy flow)
 * @property {string} status - 'pr'|'citizen'|'work_permit'|'student'|'newcomer_2y'|'other'
 * @property {'yes'|'no'|'unsure'} credit - Canadian credit history
 * @property {number} [income] - Household income
 * @property {boolean} [offerAccepted] - Has accepted an offer (buy flow)
 * @property {number} [balance] - Remaining mortgage balance (refi flow)
 * @property {number} [homeValue] - Estimated home value (refi flow)
 * @property {'lower_payment'|'pull_equity'|'switch_term'} [refiGoal] - Refinance goal
 */

/**
 * @typedef {Object} RateCandidate
 * @property {string} bankName
 * @property {number} rate
 * @property {string} term
 * @property {Object} [meta] - From bankMeta
 */

/**
 * @typedef {Object} ScoredResult
 * @property {string} bankName
 * @property {number} rate
 * @property {string} term
 * @property {number} score
 * @property {Object} meta
 * @property {string[]} reasons - i18n reason keys
 * @property {number} payment - Estimated monthly payment
 */

/**
 * @typedef {Object} FinderResult
 * @property {ScoredResult|null} recommended
 * @property {ScoredResult[]} alternatives - Up to 3
 * @property {string[]} warnings - e.g. ['warning.income_missing']
 * @property {Object} eligibility
 * @property {boolean} eligibility.insuredEligible
 * @property {boolean} eligibility.newcomerProgramLikely
 * @property {boolean} eligibility.needsAdvisor
 * @property {string[]} eligibility.missingData
 */

const NEWCOMER_STATUSES = new Set(['pr', 'work_permit', 'newcomer_2y']);
const NEWCOMER_ALL = new Set(['pr', 'work_permit', 'newcomer_2y', 'student']);

/**
 * Main entry point: find the best mortgage for a user's profile.
 *
 * @param {WizardAnswers} answers
 * @param {RateCandidate[]} candidates - From getLowestPromoRates()
 * @param {Object} bankMetaMap - bankMeta from mortgageConstants.js
 * @returns {FinderResult}
 */
export function findBestMortgage(answers, candidates, bankMetaMap) {
  if (!candidates || candidates.length === 0) {
    return {
      recommended: null,
      alternatives: [],
      warnings: [],
      eligibility: {
        insuredEligible: false,
        newcomerProgramLikely: false,
        needsAdvisor: false,
        missingData: [],
      },
    };
  }

  const warnings = [];
  const eligibility = {
    insuredEligible: false,
    newcomerProgramLikely: false,
    needsAdvisor: false,
    missingData: [],
  };

  // ── Eligibility & warnings ──
  const isBuy = answers.flow === 'buy';
  const dpPercent = isBuy && answers.price > 0
    ? (answers.downPayment / answers.price) * 100
    : 0;

  if (isBuy) {
    // Insured eligibility
    if (answers.price <= 1000000 && dpPercent >= 5) {
      eligibility.insuredEligible = true;
    }

    // DP below 5%
    if (dpPercent < 5 && answers.price > 0) {
      warnings.push('warning.dp_below_5');
      eligibility.needsAdvisor = true;
    }

    // Not insurable (>$1M)
    if (answers.price > 1000000) {
      warnings.push('warning.not_insurable');
      eligibility.insuredEligible = false;
    }
  }

  // Income missing
  if (!answers.income) {
    warnings.push('warning.income_missing');
    eligibility.missingData.push('income');
  }

  // Student — limited options
  if (answers.status === 'student') {
    warnings.push('warning.student_limited');
    eligibility.needsAdvisor = true;
  }

  // Newcomer program likely
  if (NEWCOMER_STATUSES.has(answers.status)) {
    eligibility.newcomerProgramLikely = true;
  }

  // ── Scoring ──
  const rates = candidates.map((c) => c.rate);
  const minRate = Math.min(...rates);
  const maxRate = Math.max(...rates);
  const rateSpread = maxRate - minRate || 1;

  const isNewcomerProfile = NEWCOMER_ALL.has(answers.status);
  const hasNoCredit = answers.credit !== 'yes';

  const scored = candidates.map((candidate) => {
    const meta = bankMetaMap[candidate.bankName] || {};
    let score = 0;
    const reasons = [];

    // Base score: normalize rate to 0–60 scale (lower rate = higher score)
    score += ((maxRate - candidate.rate) / rateSpread) * 60;
    reasons.push('reason.best_rate');

    // Newcomer + no credit → prefer banks with newcomer program
    if (isNewcomerProfile && hasNoCredit && meta.newcomer) {
      score += 15;
      reasons.push('reason.newcomer.no_credit');
    }

    // Credit unsure + newcomer status → prefer newcomer-program banks
    if (answers.credit === 'unsure' && isNewcomerProfile && meta.newcomer) {
      score += 10;
      reasons.push('reason.credit.unsure_newcomer');
    }

    // Quebec affinity
    if (answers.province === 'QC' && meta.qcFriendly) {
      score += 10;
      reasons.push('reason.qc_specific');
    }

    // CMHC penalty for insured mortgages
    if (isBuy && dpPercent < 20) {
      score -= 5;
    }

    // Big 5 tiebreak
    if (meta.lenderType && meta.lenderType.includes('Big 5')) {
      score += 3;
      reasons.push('reason.big5_backing');
    }

    // Newcomer program available
    if (meta.newcomer && isNewcomerProfile) {
      reasons.push('reason.newcomer_program');
    }

    // Offer accepted → prefer rate-hold banks (RBC has 120-day hold)
    if (answers.offerAccepted && meta.lenderType) {
      // RBC is known for 120-day rate hold
      if (candidate.bankName.includes('RBC')) {
        score += 8;
      }
      reasons.push('reason.offer.rate_hold');
    }

    // Refi: pull equity → prefer banks with higher LTV allowance
    if (answers.flow === 'refi' && answers.refiGoal === 'pull_equity') {
      // Big banks generally allow up to 80% LTV for equity takeout
      if (meta.lenderType && meta.lenderType.includes('Big')) {
        score += 5;
      }
      reasons.push('reason.refi.equity_ok');
    }

    // DP OK reason (if insured eligible)
    if (isBuy && eligibility.insuredEligible) {
      reasons.push('reason.insured.dp_ok');
    }

    // Calculate monthly payment
    let payment = 0;
    if (isBuy && answers.price > 0 && answers.downPayment >= 0) {
      const calc = calculateMortgage({
        homePrice: answers.price,
        downPayment: answers.downPayment,
        annualRate: candidate.rate / 100,
        amortYears: 25,
        paymentsPerYear: 12,
      });
      if (!calc.error) {
        payment = calc.paymentPerPeriod;
      }
    } else if (answers.flow === 'refi' && answers.balance > 0) {
      const calc = calculateMortgage({
        homePrice: answers.balance,
        downPayment: 0,
        annualRate: candidate.rate / 100,
        amortYears: 25,
        paymentsPerYear: 12,
      });
      if (!calc.error) {
        payment = calc.paymentPerPeriod;
      }
    }

    // Deduplicate reasons (keep order, remove duplicates)
    const uniqueReasons = [...new Set(reasons)];

    return {
      bankName: candidate.bankName,
      rate: candidate.rate,
      term: candidate.term,
      lastChange: candidate.lastChange,
      bankLogo: candidate.bankLogo,
      score,
      meta,
      reasons: uniqueReasons.slice(0, 5),
      payment,
    };
  });

  // Sort: descending by score, then ascending by rate
  scored.sort((a, b) => b.score - a.score || a.rate - b.rate);

  const recommended = scored[0] || null;
  const alternatives = scored.slice(1, 4);

  return {
    recommended,
    alternatives,
    warnings,
    eligibility,
  };
}
